<template>
  <div class="menu">
    <HUILoadingView v-if="!menu" />
    <template v-else>
      <ModalVC
        ref="burger-modal-modal"
        v-if="currentBurgerModalStandardConfig"
        :color="currentBurgerModalStandardConfig.external_color"
        :heading="currentBurgerModalStandardConfig.name"
        @close="handleModalClosed"
      >
        <HUIBurgerModal
          class="menu__burger-modal"
          @add="handleBurgerModalAdd"
          :standard-config="currentBurgerModalStandardConfig"
          :all-buns="menu.buns"
          :all-patties="menu.patties"
          :all-ingredients="menu.ingredients"
          :all-fryings="menu.fryings"
          :is-lunch="isLunch"
          :storage-origin="storageOrigin"
          :offline="offline"
        ></HUIBurgerModal>
      </ModalVC>
      <ModalVC
        ref="product-modal-modal"
        v-if="currentProduct"
        :color="currentProductColor"
        :heading="currentProduct.name"
        @close="handleModalClosed"
      >
        <HUIProductModal
          class="menu__burger-modal"
          @add="handleProductModalAdd"
          :storage-origin="storageOrigin"
          :offline="offline"
          :product="currentProduct"
        ></HUIProductModal>
      </ModalVC>

      <HUITabs class="menu__tabs" ref="tabs" :tabs="tabs">
        <HUITabsPage class="menu__tab">
          <HUIBurgerList
            class="menu__list"
            @card-click="handleBurgerListClick"
            :burgers="menu.burgers"
            :ingredients="menu.ingredients"
            :is-lunch="isLunch"
            :storage-origin="storageOrigin"
          ></HUIBurgerList>
        </HUITabsPage>

        <HUITabsPage class="menu__tab">
          <template>
            <HUIProductList
              v-for="category in accessoriesByCategory"
              :key="category.id"
              class="menu__list"
              :ref="'category-' + category.id"
              :products="category.products"
              :color="category.color"
              :heading="category.name"
              :storage-origin="storageOrigin"
              @click="handleProductListClick"
            ></HUIProductList>
          </template>
        </HUITabsPage>

        <HUITabsPage class="menu__tab">
          <template>
            <HUIProductList
              v-for="category in drinksByCategory"
              :key="category.id"
              class="menu__list"
              :ref="category.name"
              :products="category.products"
              :color="category.color"
              :heading="category.name"
              :storage-origin="storageOrigin"
              @click="handleProductListClick"
            ></HUIProductList>
          </template>
        </HUITabsPage>
      </HUITabs>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  HUIBurgerList,
  HUIBurgerModal,
  HUIProductList,
  HUIProductModal,
  HUITabs,
  HUITabsPage,
  HUILoadingView
} from '@/halifax-ui'
import ModalVC from '@/containers/ModalVC'
import { getElementVerticalMetrics } from '@/utils/getElementMetrics'
import animateProperty from '@/utils/animateProperty'
import { ORIGIN } from '@/api'

export default {
  components: {
    HUILoadingView,
    ModalVC,
    HUIBurgerList,
    HUIBurgerModal,
    HUIProductList,
    HUIProductModal,
    HUITabs,
    HUITabsPage
  },
  props: {
    isLunch: Boolean
  },
  data() {
    return {
      currentBurgerModalStandardConfig: null,
      currentProduct: null,
      tabs: ['Burgere', 'Tilbehør', 'Drikke'],
      storageOrigin: ORIGIN
    }
  },
  computed: {
    ...mapState({
      offline: 'offline',
      navbarHeight: 'navbar_height'
    }),
    // ...mapState('user', {
    //   nameOfUser: 'name',
    //   namePromptRejected: 'name_prompt_rejected'
    // }),
    ...mapState('menu', {
      menu: 'menu'
    }),
    accessoriesByCategory() {
      return this.productsByCategory('accessories')
    },
    drinksByCategory() {
      return this.productsByCategory('drinks')
    },
    productsByCategory() {
      return categoryId => {
        const categories = this.menu.product_categories.filter(
          category => category.tab === categoryId
        )
        const categoryIds = categories.map(category => category.id)
        const products = this.menu.products.filter(product =>
          categoryIds.includes(product.product_category_id)
        )
        return categories.map(category => ({
          id: category.id,
          name: category.name,
          color: category.color,
          products: products.filter(product => product.product_category_id === category.id)
        }))
      }
    },
    currentProductColor() {
      const category = this.menu.product_categories.find(
        category => category.id === this.currentProduct.product_category_id
      )
      return category ? category.color : 'light-brown'
    }
  },
  methods: {
    openBurgerModal(config) {
      this.currentBurgerModalStandardConfig = config
    },
    openProductModal(product) {
      this.currentProduct = product
    },
    resetModals() {
      this.currentBurgerModalStandardConfig = null
      this.currentProduct = null
    },
    handleModalClosed() {
      this.resetModals()
    },
    handleBurgerListClick(standardConfig) {
      this.openBurgerModal(standardConfig)
    },
    handleBurgerBowlListClick(standardConfig) {
      this.openBurgerModal(standardConfig)
    },
    handleProductListClick(id) {
      const index = this.menu.products_index[id]
      const product = this.menu.products[index]

      this.openProductModal(product)
    },
    async handleBurgerModalAdd(config, standardConfig) {
      await this.$refs['burger-modal-modal'].close()
      // await this.promptForName()

      this.scrollTo(0, () => this.$refs.tabs.showTab(1))
      this.$emit('add-burger', config, standardConfig)
    },
    async handleProductModalAdd(payload) {
      const category = this.menu.product_categories.find(
        category => category.id === payload.product_category_id
      )
      const scrollToRef = category.scroll_to
        ? this.$refs['category-' + category.scroll_to][0]
        : null

      await this.$refs['product-modal-modal'].close()
      // await this.promptForName()

      this.$emit('add-product', payload)

      if (!scrollToRef) {
        return
      }
      const { pageOffsetTop } = getElementVerticalMetrics(scrollToRef.$el)
      this.scrollTo(pageOffsetTop)
    },
    // async promptForName() {
    //   if (!this.nameOfUser && !this.namePromptRejected) {
    //     const result = await this.$store.dispatch('dialog/PROMPT', {
    //       props: {
    //         heading: 'Hvad er dit navn?',
    //         body: 'Det bliver brugt til at vise den samlede bestilling.',
    //         ok: 'Gem',
    //         cancel: 'Ikke nu',
    //         placeholder: 'Skriv dit navn'
    //       }
    //     })
    //     if (result) {
    //       this.$store.dispatch('user/REMEMBER_NAME', { name: result.input })
    //     } else {
    //       this.$store.dispatch('user/SET_NAME_PROMPT_REJECTED', true)
    //     }
    //   }
    // },
    scrollTo(offset, callback) {
      const scrollingElement = window.document.scrollingElement || window.document.documentElement

      animateProperty(
        scrollingElement,
        'scrollTop',
        Math.max(0, offset - this.navbarHeight - 20),
        callback
      )
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/variables';
@import '~@/halifax-ui/src/scss/framework';

.menu {
  $module: '.menu';

  &__tabs {
  }
  &__list {
    + #{$module}__list {
      margin-top: 10px;
    }
  }
  &__tab {
    + #{$module}__tab {
      // margin-left: 10px;
    }
  }
  &__list-heading {
    text-align: center;
    margin-top: 22px;
    margin-bottom: 22px;
  }
}
</style>
